/**
 * GUEST LIST tab — the new Portal UI (docs/plans/2026-09-host-event-edit/handoff, "Guest
 * list"). One borderless white card: `GUEST LIST · n` with the checked-in / waitlist
 * sub-line, EXPORT CSV · MESSAGE ALL · OPEN FULL LIST pills, then `hev-line` rows (40px
 * round avatar, name, meta, status pill, amount). The first four rows live here; OPEN
 * FULL LIST opens the searchable modal (desktop) or BottomSheet (mobile). Ticket holders
 * keep RESEND TICKET / REFUND CUSTOMER behind the row's `···`; stragglers keep SEND
 * REMINDER with the 24h rule; waitlist rows are read-only, as before. Row click → the
 * credentials view. Empty: NO ONE ON THE LIST YET. + SHARE EVENT (`onShare`).
 */
import React, { useMemo, useState } from 'react';
import { BottomSheet, Pill } from '../surface';
import { Card, Btn, RowText, Ava, Empty, SearchPill, Help, SubPanel } from './kit';

const DAY = 24 * 60 * 60 * 1000;

/** Row-model tones (hostOpsModel `guestRow`) → the borderless pill palette. */
const PILL_TONES = {
  black: { bg: '#000', fg: '#fff' },
  green: { bg: '#22C55E', fg: '#fff' },
  yellow: { bg: '#FFF8EA', fg: '#B45309' },
  grey: { bg: '#F2F2F2', fg: '#000' },
  outline: { bg: '#F2F2F2', fg: '#000' },
  redOutline: { bg: '#FEE2E2', fg: '#B91C1C' },
  red: { bg: '#FEE2E2', fg: '#B91C1C' },
  ghost: { bg: '#F2F2F2', fg: '#6B7280' },
  hairline: { bg: '#F2F2F2', fg: '#6B7280' },
};

/** A status pill in the new palette: radius 999, no border, 9px/700 at .14em. */
export function StatusPill({ tone, style, children, ...rest }) {
  const t = PILL_TONES[tone] || PILL_TONES.ghost;
  return (
    <Pill bg={t.bg} fg={t.fg} border="transparent" size="sm" style={{ borderRadius: 999, borderColor: 'transparent', padding: '4px 10px', letterSpacing: '0.14em', ...style }} {...rest}>
      {children}
    </Pill>
  );
}

function reminderState(app, busyId, sentNow) {
  const last = app.lastPaymentReminderAt ? new Date(app.lastPaymentReminderAt).getTime() : 0;
  const tooSoon = last > 0 && Date.now() - last < DAY;
  const justSent = !!sentNow?.[app.id];
  const busy = busyId === app.id;
  const label = busy ? 'SENDING…' : justSent ? 'SENT' : tooSoon ? 'SENT RECENTLY' : 'SEND REMINDER';
  return { tooSoon, justSent, busy, label, disabled: busy || tooSoon || justSent };
}

function agoShort(iso) {
  if (!iso) return null;
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / (60 * 60 * 1000));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const TITLE = { fontSize: 13, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' };
const SUBLINE = { fontSize: 11, letterSpacing: '0.1em', color: '#6B7280', textTransform: 'uppercase' };

/** One row. `dense` is the mobile / sheet treatment (pill and amount stack on the right). */
export function GuestListRow({ row, last = false, dense = false, showActions = true, onRowClick, onResend, onRefund, onRemind, remindBusyId, remindSent, expanded, onToggle }) {
  const { app } = row;
  const clickable = !!row.userId && !!onRowClick;
  const isHolder = app.status === 'paid';
  const isStraggler = app.status === 'pending_payment';
  const hasMenu = isHolder || (isStraggler && showActions) || (app.tickets?.length > 0);
  const rem = isStraggler ? reminderState(app, remindBusyId, remindSent) : null;
  const size = dense ? 36 : 40;

  return (
    <div data-testid={`guest-row-${row.id}`} data-last={last ? '' : undefined}>
      <div className="hev-line">
        <button
          type="button"
          onClick={clickable ? () => onRowClick(app) : undefined}
          disabled={!clickable}
          aria-label={clickable ? `Open ${row.name}` : undefined}
          style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, border: 'none', background: 'none', padding: 0, textAlign: 'left', color: 'inherit', cursor: clickable ? 'pointer' : 'default' }}
        >
          <Ava name={row.name} imageUrl={row.avatarUrl} size={size} />
          <RowText title={row.name} meta={row.meta} />
        </button>
        {dense ? (
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
            <StatusPill tone={row.pill.tone}>{row.pill.label}</StatusPill>
            <span className="hev-row-amt" style={{ fontSize: 12, minWidth: 0 }}>{row.amountText}</span>
          </span>
        ) : (
          <>
            <StatusPill tone={row.pill.tone}>{row.pill.label}</StatusPill>
            <span className="hev-row-amt">{row.amountText}</span>
          </>
        )}
        {hasMenu && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={`More for ${row.name}`}
            aria-expanded={!!expanded}
            className="hev-icon-btn"
            style={{ width: 30, height: 30, flexShrink: 0, fontSize: 13, lineHeight: 1, fontWeight: 700, background: expanded ? '#000' : '#F2F2F2', color: expanded ? '#fff' : '#000' }}
          >
            ···
          </button>
        )}
      </div>
      {expanded && (
        <SubPanel gap="12" style={{ marginBottom: 10 }} testId={`guest-detail-${row.id}`}>
          {row.email && <Help style={{ color: '#374151' }}>{row.email}</Help>}
          {app.tickets?.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 18, padding: '4px 14px', display: 'flex', flexDirection: 'column' }}>
              {app.tickets.map((t, i) => (
                <div key={t.id || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 0', fontSize: 11, borderTop: i === 0 ? 'none' : '2px solid #F2F2F2' }}>
                  <span style={{ display: 'flex', minWidth: 0, alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#9CA3AF', flexShrink: 0 }}>#{t.ticketNumber}</span>
                    <span style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.recipientName || app.guestCheckoutName || row.name}</span>
                    {t.recipientEmail && <span style={{ color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.recipientEmail}</span>}
                  </span>
                  <span style={{ display: 'flex', flexShrink: 0, alignItems: 'center', gap: 6 }}>
                    {t.isSent && <StatusPill tone="hairline">SENT</StatusPill>}
                    {t.attendanceStatus === 'attended'
                      ? <StatusPill tone="green">IN</StatusPill>
                      : <span className="hev-caps-muted" style={{ fontSize: 9, letterSpacing: '0.1em' }}>NOT CHECKED IN</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
          {isStraggler && (
            <Help caps>{app.quantity || 1} TICKET{(app.quantity || 1) > 1 ? 'S' : ''} · APPLIED {app.createdAt ? new Date(app.createdAt).toLocaleDateString() : '—'}{app.lastPaymentReminderAt ? ` · LAST REMINDER ${agoShort(app.lastPaymentReminderAt)}` : ''}</Help>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {isHolder && app.ticketCode && onResend && (
              <Btn variant="white" size="sm" onClick={() => onResend(app)}>RESEND TICKET</Btn>
            )}
            {isHolder && onRefund && (app.partyCreditsRefunded
              ? <span className="hev-caps-muted" style={{ alignSelf: 'center' }}>REFUNDED</span>
              : <Btn variant="danger" size="sm" onClick={() => onRefund(app)}>REFUND CUSTOMER</Btn>)}
            {isStraggler && showActions && onRemind && (
              <Btn variant="white" size="sm" onClick={() => onRemind(app)} disabled={rem.disabled} title={rem.tooSoon ? 'A reminder was sent within the past 24h' : ''}>{rem.label}</Btn>
            )}
          </div>
        </SubPanel>
      )}
    </div>
  );
}

function RowList({ rows, dense, rowProps }) {
  const [open, setOpen] = useState(null);
  return rows.map((row, i) => (
    <GuestListRow
      key={row.id}
      row={row}
      dense={dense}
      last={i === rows.length - 1}
      expanded={open === row.id}
      onToggle={() => setOpen((o) => (o === row.id ? null : row.id))}
      {...rowProps}
    />
  ));
}

/** OPEN FULL LIST — desktop modal (620px, radius 28, sticky header + search) or mobile sheet. */
export function HostGuestListOverlay({ open, onClose, rows, total, note, mobile, rowProps }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(s) || (r.email || '').toLowerCase().includes(s) || (r.handle || '').toLowerCase().includes(s));
  }, [rows, q]);

  const search = <SearchPill small value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this list…" aria-label="Search the guest list" />;
  const body = filtered.length === 0
    ? <Empty bare title="NO ONE MATCHES." />
    : <RowList rows={filtered} dense={mobile} rowProps={rowProps} />;

  if (mobile) {
    return (
      <BottomSheet open={open} onClose={onClose} title={`GUEST LIST · ${total}`} note={note} testId="guest-list-sheet">
        {/* The sheet portals to <body>, outside the page's `.hev` scope — re-scope so the kit's input reset applies. */}
        <div className="hev" style={{ minHeight: 0, background: '#fff' }}>
          <div style={{ padding: '14px 18px 6px' }}>{search}</div>
          <div style={{ padding: '0 18px 18px' }}>{body}</div>
        </div>
      </BottomSheet>
    );
  }
  if (!open) return null;
  return (
    <div className="fixed inset-0 safe-inset z-[80] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)', padding: '48px 24px' }} onClick={onClose} data-testid="guest-list-modal-scrim">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Guest list"
        style={{ width: '100%', maxWidth: 620, maxHeight: '100%', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 28, overflow: 'hidden', color: '#000' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="guest-list-modal"
      >
        <div style={{ flex: 'none', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '22px 26px 0' }}>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <span style={TITLE}>GUEST LIST · {total}</span>
            {note && <span style={SUBLINE}>{note}</span>}
          </span>
          <button type="button" onClick={onClose} aria-label="Close" className="hev-icon-btn" style={{ width: 34, height: 34, flexShrink: 0, fontSize: 14 }}>✕</button>
        </div>
        <div style={{ flex: 'none', padding: '14px 26px 8px' }}>{search}</div>
        <div style={{ minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', padding: '0 26px 22px' }}>{body}</div>
      </div>
    </div>
  );
}

export default function HostGuestList({ rows, total, stragglers = [], checkedIn = 0, waitlist = 0, mobile = false, showActions = true, onExport, onMessageAll, onOpenAll, onRemindAll, remindAllBusy = false, rowProps, onShare }) {
  const preview = rows.slice(0, 4);
  const anyRemindable = stragglers.some((a) => !reminderState(a, rowProps?.remindBusyId, rowProps?.remindSent).disabled);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card rise testId="guest-list-panel">
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <span style={TITLE}>GUEST LIST · {total}</span>
            <span style={SUBLINE}>{checkedIn} CHECKED IN · {waitlist} ON THE WAITLIST</span>
          </span>
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <Btn variant="secondary" size="sm" onClick={onExport} disabled={total === 0}>EXPORT CSV</Btn>
            <Btn variant="secondary" size="sm" onClick={onMessageAll}>MESSAGE ALL</Btn>
            {total > 0 && <Btn variant="primary" size="sm" onClick={onOpenAll} testId="view-all-guests">OPEN FULL LIST</Btn>}
          </span>
        </div>
        {preview.length === 0 ? (
          <Empty
            title="NO ONE ON THE LIST YET."
            action={<Btn variant="primary" size="md" style={{ height: 40, padding: '0 20px', fontSize: 11 }} onClick={() => onShare?.()}>SHARE EVENT</Btn>}
          >
            Share the event. Everyone who buys lands here with their tickets and check-in status.
          </Empty>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <RowList rows={preview} dense={mobile} rowProps={rowProps} />
          </div>
        )}
      </Card>

      {stragglers.length > 0 && (
        <Card rise gap="12" testId="stragglers-panel">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span style={TITLE}>STRAGGLERS · {stragglers.length}</span>
            {showActions && onRemindAll && (
              <Btn variant="primary" size="sm" onClick={onRemindAll} disabled={remindAllBusy || !anyRemindable}>{remindAllBusy ? 'SENDING…' : 'SEND ALL REMINDERS'}</Btn>
            )}
          </div>
          <span style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>
            These guests filled out the apply form but never finished checkout. Their spots aren't reserved. Abandoned rows auto-withdraw after 48h. Open a row's <span style={{ fontWeight: 700, color: '#000' }}>···</span> to nudge one at a time.
          </span>
        </Card>
      )}
    </div>
  );
}
